import clsx from 'clsx'
import { format, isWithinInterval } from 'date-fns'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { NavigateFunction, useNavigate, useParams } from 'react-router-dom'

import {
  ApiAccountSnapshot,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery
} from '@/api/rtk-api'
import GraphLoadingImage from '@/assets/graph-loading.svg?react'
import { Card, createAreaSeriesOptions, createLineSeriesOptions } from '@/components/core-v2'
import { PATH, TENANT } from '@/const'
import { Currency, formatCurrency, formatCurrencyFull } from '@/utilities/currency'

import classes from './index.module.scss'

const AUTOMATED_EVENTS_PLOT_LINE_ID = 'automatedEventsPlotLines'
const CLIENT_EVENTS_PLOT_LINE_ID = 'clientEventsPlotLines'

function inflightPurchases(snapshot) {
  if (snapshot.pendingPerformanceAllocation || snapshot.pendingProtectionAllocation)
    return (
      (snapshot.pendingPerformanceAllocation || 0) + (snapshot.pendingProtectionAllocation || 0)
    )
  return 0
}
export const isOperationInScope = (operation: any) =>
  operation.state === 'Completed' ||
  (operation.type === 'Dividend' && operation.state === 'Processing') ||
  (operation.type === 'ReinvestmentConfirmation' && operation.state === 'Processing')

interface optionsProps {
  accountSnapShots: ApiAccountSnapshot[]
  navigate: NavigateFunction
  source: string
  tradeId: string
}
function getOptions({
  accountSnapShots,
  navigate,
  source,
  tradeId
}: optionsProps): Highcharts.Options {
  const latestSnapshot = accountSnapShots.at(-1)
  const currency = latestSnapshot.notionalCurrency as Currency
  const { automatedEvents, clientEvents } = accountSnapShots.reduce(
    (acc, snapshot) => {
      const automated = []
      const client = []
      snapshot.operations
        .filter(o => isOperationInScope(o))
        .forEach(operation => {
          if (
            (operation.type as string) === 'FeeDeduction' ||
            operation.type === 'Rebalancing' ||
            (operation.type as string) === 'TriggeredFeeDeduction' ||
            (operation.type as string) === 'Defeasance' ||
            (operation.type as string) === 'ReinvestmentConfirmation' ||
            (operation.type as string) === 'GapPayment' ||
            (operation.type as string) === 'OrderCorrection' ||
            (operation.type as string) === 'Dividend'
          ) {
            automated.push(operation)
          } else {
            client.push(operation)
          }
        })

      if (automated.length > 0) {
        acc.automatedEvents.push({
          color: 'var(--White30)',
          width: automated.length,
          value: new Date(snapshot.valuationDate).getTime(),
          zIndex: 4,
          id: AUTOMATED_EVENTS_PLOT_LINE_ID
        })
      }

      if (client.length > 0) {
        acc.clientEvents.push({
          color: 'var(--White30)',
          dashStyle: 'Dash',
          width: client.length,
          value: new Date(snapshot.valuationDate).getTime(),
          zIndex: 4,
          id: CLIENT_EVENTS_PLOT_LINE_ID
        })
      }

      return acc
    },
    { automatedEvents: [], clientEvents: [] }
  )
  const SERIES_DATA = {
    totalValue: accountSnapShots.map(snapshot => [
      new Date(snapshot.valuationDate).getTime(),
      snapshot.accountValue
    ]),
    inflightPurchases: accountSnapShots.map(snapshot => [
      new Date(snapshot.valuationDate).getTime(),
      inflightPurchases(snapshot)
    ]),
    protectedAssetsValue: accountSnapShots.map(snapshot => [
      new Date(snapshot.valuationDate).getTime(),
      (snapshot.protectionAllocation || 0) + (snapshot.zcbAllocation || 0)
    ]),
    performanceAssetsValue: accountSnapShots.map(snapshot => [
      new Date(snapshot.valuationDate).getTime(),
      snapshot.performanceAllocation || 0
    ]),
    protectedAmount: accountSnapShots.map(snapshot => [
      new Date(snapshot.valuationDate).getTime(),
      snapshot.protectedAmount
    ]),
    automatedEvents,
    clientEvents
  }

  const series: Highcharts.SeriesOptionsType[] = [
    createAreaSeriesOptions(draft => {
      draft.data = SERIES_DATA.inflightPurchases
      draft.stacking = 'normal'
      draft.color = 'var(--White80)'
      draft.name = 'Inflight'
      draft.animation = false
      draft.legendIndex = 4
    }),
    createAreaSeriesOptions(draft => {
      draft.data = SERIES_DATA.protectedAssetsValue
      draft.name = 'Protection assets'
      draft.id = 'protectedAssetsValue'
      draft.stacking = 'normal'
      draft.color = '#736464'
      draft.legendIndex = 1
      draft.fillColor = '#736464'
    }),
    createAreaSeriesOptions(draft => {
      draft.data = SERIES_DATA.performanceAssetsValue
      draft.name = 'Performance assets'
      draft.id = 'performanceAssetsValue'
      draft.stacking = 'normal'
      draft.color = 'var(--Crimson-Light)'
      draft.legendIndex = 2
      draft.fillColor = {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0, 'var(--Crimson-Light)'],
          [1, '#df5a66']
        ]
      }
    }),
    createLineSeriesOptions(draft => {
      draft.data = SERIES_DATA.totalValue
      draft.name = 'Total account value'
      draft.id = 'accountValue'
      draft.color = 'black'
      draft.animation = false
      draft.type = 'line'
      draft.stacking = 'overlap'
      draft.legendIndex = 0
    }),
    {
      color: 'var(--White20)',
      name: 'Automated events',
      type: 'line',
      legendIndex: 5,
      marker: {
        enabled: false
      },
      visible: false,
      events: {
        legendItemClick() {
          if (this.visible) {
            this.chart.xAxis[0].removePlotLine(AUTOMATED_EVENTS_PLOT_LINE_ID)
          } else {
            SERIES_DATA.automatedEvents.forEach(event => {
              this.chart.xAxis[0].addPlotLine(event)
            })
          }
        }
      }
    },
    {
      color: 'var(--White20)',
      dashStyle: 'Dash',
      name: 'Client events',
      type: 'line',
      legendIndex: 6,
      marker: {
        enabled: false
      },
      visible: false,
      events: {
        legendItemClick() {
          if (this.visible) {
            this.chart.xAxis[0].removePlotLine(CLIENT_EVENTS_PLOT_LINE_ID)
          } else {
            SERIES_DATA.clientEvents.forEach(event => {
              this.chart.xAxis[0].addPlotLine(event)
            })
          }
        }
      }
    }
  ]
  if (SERIES_DATA.protectedAmount.some(point => point[1] > 0)) {
    series.push(
      createLineSeriesOptions(draft => {
        draft.data = SERIES_DATA.protectedAmount
        draft.color = 'var(--White20)'
        draft.name = 'Protected amount'
        draft.dashStyle = 'ShortDash'
        draft.legendIndex = 3
      })
    )
  }

  return {
    chart: {
      type: 'area',
      height: 500,
      backgroundColor: 'none',
      borderColor: 'none',
      borderWidth: 0,
      events: {
        click(e: any) {
          const timestamp = e?.xAxis?.[0]?.value
          if (timestamp) {
            navigate(PATH.reportsSingleTradeEventsHoldings(source, tradeId, new Date(timestamp)))
          }
        }
      },
      zooming: {
        type: 'x'
      }
    },
    plotOptions: {
      area: {
        stacking: 'normal'
      }
    },
    series,
    scrollbar: {
      enabled: false
    },
    legend: {
      enabled: true,
      align: 'left',
      x: 0,
      margin: 24,
      itemStyle: {
        fontSize: '16px',
        fontWeight: '400',
        cursor: 'pointer',
        lineHeight: '16px',
        transform: 'translateY(-2px)'
      }
    },
    tooltip: {
      formatter() {
        const { series, x: timestamp, y: value } = this
        return `<div>
          <p>${format(new Date(timestamp), 'EEEE d MMMM yyyy')}</p>
          <p>${series.name}: ${formatCurrencyFull(value, currency)}</p>
        </div>`
      },
      className: 'tooltip',
      enabled: true,
      animation: false,
      backgroundColor: 'var(--White)',
      borderColor: 'var(--White20)',
      borderRadius: 8,
      borderWidth: 1.5,
      padding: 12,
      shape: 'callout',
      shadow: false,
      useHTML: true,
      distance: 32,
      style: {
        fontSize: '15'
      }
    },
    title: {
      text: undefined
    },
    xAxis: {
      labels: {
        y: 32
      },
      dateTimeLabelFormats: {
        day: '%e %B',
        week: '%e %B',
        month: '%B %Y',
        year: '%Y'
      },
      className: 'xaxis-label',
      tickPixelInterval: 200,
      title: null,
      type: 'datetime',
      minPadding: 0
    },
    yAxis: [
      {
        labels: {
          align: 'left',
          y: 4,
          x: 0,
          formatter() {
            return `<span class='yaxis-label'>${
              this.value ? formatCurrency(Number(this.value), currency) : 0
            }</span>`
          }
        },
        min: 0,
        minPadding: 0,
        offset: 10,
        opposite: true,
        title: null
      }
    ]
  }
}

interface Props {
  dateRange: [Date, Date]
}

export const TradePerformanceChart = ({ dateRange }: Props) => {
  const { source, tradeId } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery(
    {
      tenant: TENANT,
      accountIdentifier: tradeId,
      accountSource: source
    },
    {
      selectFromResult: ({ data, ...props }) => ({
        ...props,
        data:
          data &&
          data.accountSnapShots.filter(snapshot =>
            isWithinInterval(new Date(snapshot.valuationDate), {
              start: new Date(dateRange[0]),
              end: new Date(dateRange[1])
            })
          )
      })
    }
  )

  if (isLoading)
    return (
      <Card.Wrapper className={clsx('mt-4', classes.skeleton)}>
        <GraphLoadingImage />
      </Card.Wrapper>
    )

  return (
    <Card.Wrapper className={clsx('mt-4', classes.chartWrapper)}>
      <HighchartsReact
        highcharts={Highcharts}
        options={getOptions({ accountSnapShots: data, source, tradeId, navigate })}
      />
    </Card.Wrapper>
  )
}
